import { computed, ref, useSlots, type VNode, watch } from 'vue';

type GetTextOptions = { skipAriaHidden?: boolean };

type UseSlotTextArgs = {
  name: string;
  slotProps?: Record<string, unknown>;
  opts?: GetTextOptions;
};

// get text from every element in the slot recursively
export const getText = (nodes?: VNode[], opts?: GetTextOptions): string => {
  // default to true, slot text is typically an accessible name
  const skipAriaHidden = opts?.skipAriaHidden ?? true;
  const parts: string[] = [];
  nodes?.forEach((node) => {
    const ariaHidden = node.props?.['aria-hidden'];
    if (skipAriaHidden && (ariaHidden === 'true' || ariaHidden === true)) return;
    if (typeof node.children === 'string') {
      const trimmed = node.children.trim();
      if (trimmed) parts.push(trimmed);
    } else if (Array.isArray(node.children) && node.children.length) {
      const nested = getText(node.children as VNode[], opts);
      if (nested) parts.push(nested);
    }
  });
  return parts.join(' ');
};

export const useSlotText = ({ name, slotProps, opts }: UseSlotTextArgs) => {
  const slot = useSlots()[name];
  const text = ref('');

  if (slot) {
    text.value = getText(slot(slotProps), opts);
  }

  //watch for changes and update
  watch(
    () => useSlots()[name],
    () => {
      const slot = useSlots()[name];
      if (slot) text.value = getText(slot(slotProps), opts);
    },
  );

  return computed(() => text.value);
};
